const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

async function fetchAPI(url: string, options: RequestInit = {}): Promise<any> {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  const response = await fetch(url, { ...options, headers });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

export interface UserStats {
  summary: {
    totalCourses: number;
    completedCourses: number;
    inProgressCourses: number;
    avgProgress: number;
    avgScore: number | null;
    totalTimeSpent: number;
    certificatesEarned: number;
    badgesEarned: number;
  };
  courses: Array<{
    id: number;
    status: string;
    progressPercentage: number;
    score: number | null;
    enrolledAt: string;
    completedAt: string | null;
    timeSpentSeconds: number;
    courseId: number;
    courseTitle: string;
  }>;
  recentCompletions: Array<{
    lessonTitle: string;
    courseTitle: string;
    completedAt: string;
    score: number | null;
  }>;
}

export interface CompanyStats {
  summary: {
    totalEnrollments: number;
    totalLearners: number;
    completedCount: number;
    inProgressCount: number;
    avgProgress: number;
    totalTimeSpent: number;
    certificatesIssued: number;
    badgesEarned: number;
  };
  courses: Array<{
    id: number;
    title: string;
    enrollmentCount: number;
    completedCount: number;
    avgProgress: number;
    avgScore: number | null;
    qualityScore: number | null;
    qualityCheckedAt: string | null;
  }>;
}

export interface InstructorStats {
  summary: {
    totalCourses: number;
    totalEnrollments: number;
    totalParticipants: number;
    completedCount: number;
    avgProgress: number;
    avgScore: number | null;
  };
  courses: Array<{
    id: number;
    title: string;
    enrollmentCount: number;
    completedCount: number;
    avgProgress: number;
    avgScore: number | null;
    qualityScore: number | null;
    qualityCheckedAt: string | null;
  }>;
}

export interface TrendData {
  period: number;
  enrollments: Array<{ date: string; count: number }>;
  completions: Array<{ date: string; count: number }>;
  timeByDay: Array<{ date: string; timeSpent: number }>;
  lessonCompletions: Array<{ date: string; count: number }>;
}

export interface CourseStats {
  summary: {
    totalEnrollments: number;
    completedCount: number;
    inProgressCount: number;
    avgProgress: number;
    avgScore: number | null;
    avgTimeSpent: number;
  };
  lessons: Array<{
    id: number;
    title: string;
    sortOrder: number;
    attempts: number;
    completions: number;
    avgScore: number | null;
    avgTime: number;
  }>;
}

export interface DashboardData {
  activeCourses: Array<{
    id: number;
    courseId: number;
    courseTitle: string;
    progress: number;
    lastAccessed: string | null;
  }>;
  recentCompletions: Array<{
    lessonTitle: string;
    courseTitle: string;
    completedAt: string;
    score: number | null;
  }>;
  weekSummary: {
    timeSpentMinutes: number;
    lessonsCompleted: number;
  };
}

class AnalyticsService {
  /**
   * Get current user's statistics
   */
  async getMyStats(): Promise<UserStats> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/my`);
  }

  /**
   * Get company-wide statistics (admin only)
   */
  async getCompanyStats(): Promise<CompanyStats> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/company`);
  }

  /**
   * Get instructor statistics (instructor only)
   */
  async getInstructorStats(): Promise<InstructorStats> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/instructor`);
  }

  /**
   * Get time-based trends
   */
  async getTrends(period: number = 30): Promise<TrendData> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/trends?period=${period}`);
  }

  /**
   * Get course-specific statistics
   */
  async getCourseStats(courseId: number): Promise<CourseStats> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/course/${courseId}`);
  }

  /**
   * Get dashboard data
   */
  async getDashboard(period: number = 30): Promise<DashboardData> {
    return fetchAPI(`${API_BASE_URL}/api/statistics/dashboard?period=${period}`);
  }

  /**
   * Trigger AI quality check for a course (instructor/admin only)
   */
  async triggerQualityCheck(courseId: number): Promise<any> {
    return fetchAPI(`${API_BASE_URL}/api/courses/${courseId}/quality-check`, { method: 'POST' });
  }

  /**
   * Get existing quality check result for a course
   */
  async getQualityCheck(courseId: number): Promise<any> {
    return fetchAPI(`${API_BASE_URL}/api/courses/${courseId}/quality-check`);
  }

  /**
   * Format time in seconds to human-readable string
   */
  formatTime(seconds: number): string {
    if (!seconds || seconds < 60) return '< 1 Min.';

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} Min.`;
  }

  /**
   * Calculate completion rate as percentage
   */
  calculateCompletionRate(completed: number, total: number): number {
    if (total === 0) return 0;
    return Math.round((completed / total) * 100);
  }

  /**
   * Fill missing dates in trend data
   */
  fillMissingDates(
    data: Array<{ date: string; count?: number; timeSpent?: number; value?: number }>,
    period: number,
    valueKey: 'count' | 'timeSpent' | 'value' = 'count'
  ): Array<{ date: string; value: number }> {
    const result: Array<{ date: string; value: number }> = [];
    const dataMap = new Map(data.map(d => [d.date, d[valueKey] || 0]));

    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - period);

    for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().split('T')[0];
      result.push({
        date: dateStr,
        value: dataMap.get(dateStr) || 0,
      });
    }

    return result;
  }
}

export const analyticsService = new AnalyticsService();
export default analyticsService;
